import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  IconButton,
  Tooltip
} from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CancelIcon from '@mui/icons-material/Cancel';

// Mutfak ekranı ayarları için tip tanımları
export interface OrderCardColorSettings {
  // Beklemede durumu - süreye göre renklendirme
  pending: {
    '0-5': { backgroundColor: string; borderColor: string; timeColor: string };
    '6-15': { backgroundColor: string; borderColor: string; timeColor: string };
    '16-25': { backgroundColor: string; borderColor: string; timeColor: string };
    '25+': { backgroundColor: string; borderColor: string; timeColor: string };
  };
  // Diğer durumlar
  preparing: { backgroundColor: string; borderColor: string; timeColor: string };
  ready: { backgroundColor: string; borderColor: string; timeColor: string };
  delivered: { backgroundColor: string; borderColor: string; timeColor: string };
  cancelled: { backgroundColor: string; borderColor: string; timeColor: string };
}

export interface KitchenScreenSettingsData {
  orderCardColors: OrderCardColorSettings;
  timeRanges: {
    range1: number; // 0-X dakika (varsayılan 5)
    range2: number; // X-Y dakika (varsayılan 15) 
    range3: number; // Y-Z dakika (varsayılan 25)
    // Z+ dakika otomatik
  };
}

interface KitchenScreenSettingsProps {
  settings: KitchenScreenSettingsData;
  onSettingsChange: (newSettings: KitchenScreenSettingsData) => void;
}

// Varsayılan renk ayarları
const defaultColorSettings: OrderCardColorSettings = {
  pending: {
    '0-5': { 
      backgroundColor: '#e8f5e8', 
      borderColor: '#4caf50', 
      timeColor: '#2e7d32' 
    },
    '6-15': { 
      backgroundColor: '#fff3e0', 
      borderColor: '#ff9800', 
      timeColor: '#f57c00' 
    },
    '16-25': { 
      backgroundColor: '#fce4ec', 
      borderColor: '#e91e63', 
      timeColor: '#c2185b' 
    },
    '25+': { 
      backgroundColor: '#ffebee', 
      borderColor: '#f44336', 
      timeColor: '#d32f2f' 
    }
  },
  preparing: { 
    backgroundColor: '#fff8e1', 
    borderColor: '#ffc107', 
    timeColor: '#f9a825' 
  },
  ready: { 
    backgroundColor: '#e8f5e8', 
    borderColor: '#4caf50', 
    timeColor: '#2e7d32' 
  },
  delivered: { 
    backgroundColor: '#f3e5f5', 
    borderColor: '#9c27b0', 
    timeColor: '#7b1fa2' 
  },
  cancelled: { 
    backgroundColor: '#fafafa', 
    borderColor: '#9e9e9e', 
    timeColor: '#616161' 
  }
};

const defaultTimeRanges = {
  range1: 5,   // 0-5 dakika
  range2: 15,  // 6-15 dakika  
  range3: 25   // 16-25 dakika (25+ otomatik)
};

const KitchenScreenSettings: React.FC<KitchenScreenSettingsProps> = ({
  settings,
  onSettingsChange
}) => {
  const { t } = useTranslation('salesSystem');
  const [selectedColorCategory, setSelectedColorCategory] = useState<string | null>(null);
  const [selectedColorType, setSelectedColorType] = useState<'backgroundColor' | 'borderColor' | 'timeColor'>('backgroundColor');
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Ayarları güncelleme fonksiyonu
  const updateSettings = (newSettings: Partial<KitchenScreenSettingsData>) => {
    onSettingsChange({
      ...settings,
      ...newSettings
    });
  };

  // Renk değiştirme fonksiyonu
  const handleColorChange = (color: string) => {
    if (!selectedColorCategory) return;

    const newColorSettings = { ...settings.orderCardColors };
    
    if (selectedColorCategory.includes('-')) {
      // Beklemede durumu için süre aralığı
      const parts = selectedColorCategory.split('-');
      const range = parts.slice(1).join('-'); // "0-5", "6-15", "16-25", "25+" için
      const rangeKey = range as keyof typeof newColorSettings.pending;
      
      if (newColorSettings.pending[rangeKey]) {
        newColorSettings.pending[rangeKey] = {
          ...newColorSettings.pending[rangeKey],
          [selectedColorType]: color
        };
      }
    } else {
      // Diğer durumlar
      const statusKey = selectedColorCategory as keyof Omit<OrderCardColorSettings, 'pending'>;
      if (newColorSettings[statusKey]) {
        newColorSettings[statusKey] = {
          ...newColorSettings[statusKey],
          [selectedColorType]: color
        };
      }
    }

    updateSettings({ orderCardColors: newColorSettings });
  };

  // Süre aralığı değiştirme fonksiyonu
  const handleTimeRangeChange = (rangeKey: keyof typeof settings.timeRanges, value: number) => {
    updateSettings({
      timeRanges: {
        ...settings.timeRanges,
        [rangeKey]: value
      }
    });
  };

  // Renk seçici açma fonksiyonu
  const openColorPicker = (category: string, colorType: 'backgroundColor' | 'borderColor' | 'timeColor') => {
    console.log('Renk seçici açılıyor:', { category, colorType });
    setSelectedColorCategory(category);
    setSelectedColorType(colorType);
    setShowColorPicker(true);
  };

  // Mevcut rengi güvenli şekilde alma fonksiyonu
  const getCurrentColor = (): string => {
    if (!selectedColorCategory || !selectedColorType) return '#000000';
    
    try {
      if (selectedColorCategory.includes('-')) {
        const parts = selectedColorCategory.split('-');
        const range = parts.slice(1).join('-'); // "0-5", "6-15", "16-25", "25+" için
        const rangeKey = range as keyof typeof settings.orderCardColors.pending;
        const colorValue = settings.orderCardColors.pending[rangeKey]?.[selectedColorType];
        console.log('Pending renk değeri:', { range, rangeKey, colorValue });
        return colorValue || '#000000';
      } else {
        const statusKey = selectedColorCategory as keyof Omit<OrderCardColorSettings, 'pending'>;
        const colorValue = settings.orderCardColors[statusKey]?.[selectedColorType];
        console.log('Status renk değeri:', { statusKey, colorValue });
        return colorValue || '#000000';
      }
    } catch (error) {
      console.error('Renk değeri alınırken hata:', error, { selectedColorCategory, selectedColorType });
      return '#000000';
    }
  };

  // Süreleri varsayılan ayarlara sıfırlama
  const resetTimeRangesToDefaults = () => {
    updateSettings({
      timeRanges: { ...defaultTimeRanges }
    });
  };

  // Renkleri varsayılan ayarlara sıfırlama
  const resetColorsToDefaults = () => {
    updateSettings({
      orderCardColors: JSON.parse(JSON.stringify(defaultColorSettings)) // Deep copy
    });
  };

  // Renk kartı bileşeni
  const ColorCard: React.FC<{
    title: string;
    subtitle?: string;
    colors: { backgroundColor: string; borderColor: string; timeColor: string };
    category: string;
    icon?: React.ReactNode;
  }> = ({ title, subtitle, colors, category, icon }) => (
    <Card 
      variant="outlined" 
      sx={{ 
        mb: 2,
        border: `2px solid ${colors.borderColor}`,
        backgroundColor: colors.backgroundColor,
        transition: 'all 0.2s ease'
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {icon && <Box sx={{ mr: 1, color: colors.timeColor }}>{icon}</Box>}
            <Typography variant="subtitle2" fontWeight="bold">
              {title}
            </Typography>
          </Box>
          <Typography 
            variant="caption" 
            sx={{ 
              color: colors.timeColor,
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}
          >
            15 dk
          </Typography>
        </Box>
        
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {subtitle}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title={t('kitchenScreenSettings.colorTooltips.background')}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => openColorPicker(category, 'backgroundColor')}
              sx={{
                minWidth: 'auto',
                width: 32,
                height: 24,
                p: 0,
                backgroundColor: colors.backgroundColor,
                borderColor: colors.backgroundColor,
                '&:hover': {
                  backgroundColor: colors.backgroundColor,
                  opacity: 0.8
                }
              }}
            />
          </Tooltip>
          
          <Tooltip title={t('kitchenScreenSettings.colorTooltips.border')}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => openColorPicker(category, 'borderColor')}
              sx={{
                minWidth: 'auto',
                width: 32,
                height: 24,
                p: 0,
                backgroundColor: colors.borderColor,
                borderColor: colors.borderColor,
                '&:hover': {
                  backgroundColor: colors.borderColor,
                  opacity: 0.8
                }
              }}
            />
          </Tooltip>
          
          <Tooltip title={t('kitchenScreenSettings.colorTooltips.time')}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => openColorPicker(category, 'timeColor')}
              sx={{
                minWidth: 'auto',
                width: 32,
                height: 24,
                p: 0,
                backgroundColor: colors.timeColor,
                borderColor: colors.timeColor,
                '&:hover': {
                  backgroundColor: colors.timeColor,
                  opacity: 0.8
                }
              }}
            />
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('kitchenScreenSettings.title')}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {t('kitchenScreenSettings.subtitle')}
      </Typography>

      <Grid container spacing={3}>
        {/* Sol Taraf: Süre Aralığı Ayarları */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('kitchenScreenSettings.timeRanges.title')}
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                {t('kitchenScreenSettings.timeRanges.range1', { max: settings.timeRanges.range1 })}
              </Typography>
              <Slider
                value={settings.timeRanges.range1}
                min={1}
                max={10}
                step={1}
                onChange={(_, value) => handleTimeRangeChange('range1', value as number)}
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />
              
              <Typography variant="body2" gutterBottom>
                {t('kitchenScreenSettings.timeRanges.range2', {
                  min: settings.timeRanges.range1 + 1,
                  max: settings.timeRanges.range2
                })}
              </Typography>
              <Slider
                value={settings.timeRanges.range2}
                min={settings.timeRanges.range1 + 1}
                max={30}
                step={1}
                onChange={(_, value) => handleTimeRangeChange('range2', value as number)}
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />
              
              <Typography variant="body2" gutterBottom>
                {t('kitchenScreenSettings.timeRanges.range3', {
                  min: settings.timeRanges.range2 + 1,
                  max: settings.timeRanges.range3
                })}
              </Typography>
              <Slider
                value={settings.timeRanges.range3}
                min={settings.timeRanges.range2 + 1}
                max={60}
                step={1}
                onChange={(_, value) => handleTimeRangeChange('range3', value as number)}
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />
              
              <Typography variant="body2" color="text.secondary">
                {t('kitchenScreenSettings.timeRanges.range4', { min: settings.timeRanges.range3 + 1 })}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              onClick={resetTimeRangesToDefaults}
              fullWidth
            >
              {t('kitchenScreenSettings.actions.resetTimes')}
            </Button>
          </Paper>
        </Grid>

        {/* Orta: Beklemede Durumu Renkleri */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              <RestaurantIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('kitchenScreenSettings.pendingStatus.title')}
            </Typography>
            
            <ColorCard
              title={t('kitchenScreenSettings.pendingStatus.newOrder')}
              subtitle={t('kitchenScreenSettings.timeLabels.rangeStart', { max: settings.timeRanges.range1 })}
              colors={settings.orderCardColors.pending['0-5']}
              category="pending-0-5"
              icon={<Chip label={t('kitchenScreenSettings.badges.new')} size="small" color="success" />}
            />
            
            <ColorCard
              title={t('kitchenScreenSettings.pendingStatus.normalWait')}
              subtitle={t('kitchenScreenSettings.timeLabels.rangeBetween', {
                min: settings.timeRanges.range1 + 1,
                max: settings.timeRanges.range2
              })}
              colors={settings.orderCardColors.pending['6-15']}
              category="pending-6-15"
              icon={<Chip label={t('kitchenScreenSettings.badges.normal')} size="small" color="warning" />}
            />
            
            <ColorCard
              title={t('kitchenScreenSettings.pendingStatus.delayed')}
              subtitle={t('kitchenScreenSettings.timeLabels.rangeBetween', {
                min: settings.timeRanges.range2 + 1,
                max: settings.timeRanges.range3
              })}
              colors={settings.orderCardColors.pending['16-25']}
              category="pending-16-25"
              icon={<Chip label={t('kitchenScreenSettings.badges.delay')} size="small" color="error" />}
            />
            
            <ColorCard
              title={t('kitchenScreenSettings.pendingStatus.criticalDelay')}
              subtitle={t('kitchenScreenSettings.timeLabels.rangeAbove', { min: settings.timeRanges.range3 + 1 })}
              colors={settings.orderCardColors.pending['25+']}
              category="pending-25+"
              icon={<Chip label={t('kitchenScreenSettings.badges.critical')} size="small" sx={{ bgcolor: '#f44336', color: 'white' }} />}
            />
          </Paper>
        </Grid>

        {/* Sağ Taraf: Diğer Durumlar */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('kitchenScreenSettings.otherStatuses.title')}
            </Typography>
            
            <ColorCard
              title={t('kitchenScreenSettings.otherStatuses.preparing.title')}
              subtitle={t('kitchenScreenSettings.otherStatuses.preparing.subtitle')}
              colors={settings.orderCardColors.preparing}
              category="preparing"
              icon={<RestaurantIcon />}
            />
            
            <ColorCard
              title={t('kitchenScreenSettings.otherStatuses.ready.title')}
              subtitle={t('kitchenScreenSettings.otherStatuses.ready.subtitle')}
              colors={settings.orderCardColors.ready}
              category="ready"
              icon={<CheckCircleIcon />}
            />
            
            <ColorCard
              title={t('kitchenScreenSettings.otherStatuses.delivered.title')}
              subtitle={t('kitchenScreenSettings.otherStatuses.delivered.subtitle')}
              colors={settings.orderCardColors.delivered}
              category="delivered"
              icon={<LocalShippingIcon />}
            />
            
            <ColorCard
              title={t('kitchenScreenSettings.otherStatuses.cancelled.title')}
              subtitle={t('kitchenScreenSettings.otherStatuses.cancelled.subtitle')}
              colors={settings.orderCardColors.cancelled}
              category="cancelled"
              icon={<CancelIcon />}
            />

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={resetColorsToDefaults}
                fullWidth
              >
                {t('kitchenScreenSettings.actions.resetColors')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Renk Seçici Modal */}
      {showColorPicker && selectedColorCategory && (
        <Paper
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            p: 3,
            boxShadow: 24,
            borderRadius: 2,
            minWidth: 300
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {t('kitchenScreenSettings.colorPicker.title')}
            </Typography>
            <IconButton onClick={() => setShowColorPicker(false)}>
              <CancelIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedColorType === 'backgroundColor' && t('kitchenScreenSettings.colorPicker.background')}
            {selectedColorType === 'borderColor' && t('kitchenScreenSettings.colorPicker.border')}
            {selectedColorType === 'timeColor' && t('kitchenScreenSettings.colorPicker.time')}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <HexColorPicker
              color={getCurrentColor()}
              onChange={handleColorChange}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => setShowColorPicker(false)}
            >
              {t('kitchenScreenSettings.common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => setShowColorPicker(false)}
            >
              {t('kitchenScreenSettings.common.ok')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Overlay */}
      {showColorPicker && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998
          }}
          onClick={() => setShowColorPicker(false)}
        />
      )}
    </Box>
  );
};

export default KitchenScreenSettings;
